import { useEffect } from 'react';
import i18n from 'i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getActiveSiteAsync,
  getUserClaimsAsync,
  selectActiveSite,
  selectActiveSiteStatus,
  selectAuthClaims,
  selectAuthStatus,
  selectIdentityClaims,
  selectLoginName,
  selectLogoutUrl,
  selectRoleClaims,
  selectSiteClaims,
} from './authSlice';
import { LOADING_STATUS } from '../../common/constants';

export const useAuthUser = () => {
  const { NODE_ENV } = process.env;

  const dispatch = useAppDispatch();
  const identityClaims = useAppSelector(selectIdentityClaims);
  const authClaims = useAppSelector(selectAuthClaims);
  const roleClaims = useAppSelector(selectRoleClaims);
  const siteClaims = useAppSelector(selectSiteClaims);
  const activeSite = useAppSelector(selectActiveSite);
  const activeSiteStatus = useAppSelector(selectActiveSiteStatus);
  const authStatus = useAppSelector(selectAuthStatus);

  const { firstName, lastName } = useAppSelector(selectLoginName);
  const logoutUrl = useAppSelector(selectLogoutUrl);

  useEffect(() => {
    if (!identityClaims) {
      dispatch(getUserClaimsAsync());
    }
  }, [dispatch, identityClaims]);

  useEffect(() => {
    if (
      activeSiteStatus === LOADING_STATUS.IDLE &&
      !activeSite &&
      authStatus === LOADING_STATUS.IDLE &&
      siteClaims &&
      siteClaims.length > 0
    ) {
      dispatch(getActiveSiteAsync());
    }
  }, [dispatch, activeSiteStatus, activeSite, authStatus, siteClaims]);

  useEffect(() => {
    if (
      authStatus === LOADING_STATUS.FAILED &&
      !identityClaims &&
      window.location.pathname !== '/'
    ) {
      // Store the current location path in localStorage for redirect after login
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
    }
  }, [authStatus, identityClaims, dispatch]);

  const loginUrl =
    NODE_ENV === 'production'
      ? '/bff/login?returnUrl=/'
      : 'https://localhost:7007/bff/login?returnUrl=/';

  const isLoggedIn = identityClaims && authStatus === LOADING_STATUS.IDLE;

  const fullLogoutUrl =
    NODE_ENV === 'production'
      ? logoutUrl
      : `https://localhost:7007${logoutUrl}`;
  let isTravelAgencyOnlyUser = false;

  let hasPermissions =
    authClaims &&
    authClaims?.length > 0 &&
    siteClaims &&
    siteClaims?.length > 0;

  if (hasPermissions) {
    const rolesToCheck = [
      'UserAdministrator',
      'AccommodationAdministrator',
      'PaymentAdministrator',
      'SettingAdministrator',
      'BookingAdministrator',
      'FinancialManagementAdministrator',
      'LocationAdministrator',
      'PricingAdministrator',
      'CustomerFeedbackAdministrator',
      'TravelAgency',
    ];

    hasPermissions =
      roleClaims?.some(role => rolesToCheck.includes(role)) || false;

    // Detect specifically TravelAgency role
    isTravelAgencyOnlyUser = roleClaims?.includes('TravelAgency') ?? false;
  }
  // if(hasPermissions()){
  // If user has TravelAgency Role -> change language to English
  if (isTravelAgencyOnlyUser && i18n.language !== 'en') {
    i18n.changeLanguage('en');
  }

  // }
  return {
    loginFirstName: firstName,
    loginLastName: lastName,
    isLoggedIn,
    isLoading:
      authStatus === LOADING_STATUS.LOADING ||
      activeSiteStatus === LOADING_STATUS.LOADING,
    loginUrl,
    logoutUrl: fullLogoutUrl,
    hasPermissions,
    roleClaims,
    siteClaims,
    activeSite,
    identityClaims,
  };
};
